import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import { defineStore } from "pinia";
import type { IFavourite } from "~/types/player/sport-favourite";
import type { IEvent } from "~/types/sports/event";
import type { IMarket } from "~/types/sports/market";
import type { IOutcome } from "~/types/sports/outcome";
import type { IPrice } from "~/types/sports/price";
import type { IProducer } from "~/types/sports/producer";
import type { IRegion } from "~/types/sports/region";
import type { IScore } from "~/types/sports/score";
import type { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import { useSettings } from "./settings";

interface IBetBook {
	activeEvents: number[];
	widgets: {
		stream: boolean;
		info: boolean;
		tips: boolean;
	};
	oddsFormat: "decimal" | "fractional";
	applyingFilters: boolean;
	streamableLeagues: { [x: string]: { sportProvider: string; leagueId: string; sportCountry: string }[] } | null;
	isTipsAvailable: boolean;
	filterModal: "type-sort" | "leagues" | "date" | "odds" | boolean;
	displayMarkets: { name: string; displayName: string; filterIndex: number }[];
	filters: {
		sortOrder: "League" | "Time" | undefined;
		boostedOnly: boolean;
		odds:
			| {
					maximum: number;
					minimum: number;
			  }
			| undefined;
		paging: {
			skip: number;
			take?: number;
		};
		fromStartEpoch: number | undefined;
		toStartEpoch: number | undefined;
		regionAndLeagueIds:
			| {
					regionId: string;
					leagueId: string;
			  }[]
			| undefined;
		markets: { name: string; displayName: string; filterIndex: number }[];
	};
	favourites: {
		league: IFavourite[];
		market: IFavourite[];
	};
	producerStatus: { [x: string]: boolean };
	events: Map<number, IEvent>;
	markets: Map<string, IMarket>;
	outcomes: Map<string, IOutcome>;
	prices: Map<string, IPrice>;
	scores: Map<number, IScore>;
	regions: Map<string, IRegion>;
	sportsBonusAllowedSports: ISport[];
	pointers: {
		events: number[];
		markets: Map<number, string[]>;
		outcomes: Map<string, string[]>;
	};
}

export const useBetbook = defineStore({
	id: "betbook",
	state: (): IBetBook => ({
		widgets: {
			stream: false,
			info: false,
			tips: false,
		},
		oddsFormat: "decimal",
		producerStatus: {},
		streamableLeagues: null,
		displayMarkets: [],
		isTipsAvailable: false,
		favourites: {
			league: [],
			market: [],
		},
		activeEvents: [],
		filterModal: false,
		applyingFilters: false,
		filters: {
			odds: undefined,
			fromStartEpoch: undefined,
			toStartEpoch: undefined,
			sortOrder: "League",
			boostedOnly: false,
			markets: [],
			regionAndLeagueIds: undefined,
			paging: {
				skip: 0,
				take: 20,
			},
		},
		events: new Map(),
		markets: new Map(),
		outcomes: new Map(),
		prices: new Map(),
		scores: new Map(),
		regions: new Map(),
		sportsBonusAllowedSports: [],
		pointers: {
			events: [],
			markets: new Map(),
			outcomes: new Map(),
		},
	}),
	actions: {
		createEmopPointers() {
			const events = [...this.events.values()];
			this.pointers.events = events.map((x) => x.eventId);
			events.forEach((event) => {
				// Find active markets for this event
				// Set markets pointers
				const markets = [...this.markets.values()].filter((m) => m.eventId === event.eventId).map((m) => m.marketId);

				this.pointers.markets.set(event.eventId, markets);
				markets.forEach((m) => {
					const outcomes = [...this.outcomes.values()]
						.filter((o) => (o.originalMarketId ? o.originalMarketId === m : o.marketId === m))
						.sort((a, b) => a.index - b.index)
						.map((m) => m.outcomeId);
					this.pointers.outcomes.set(m, outcomes);
				});
			});
		},
		resetFilters() {
			this.filters.boostedOnly = false;
			this.filters.sortOrder = "League";
			this.filters.regionAndLeagueIds = undefined;
			this.filters.fromStartEpoch = undefined;
			this.filters.toStartEpoch = undefined;
			this.filters.odds = undefined;
			this.filters.markets = [];
		},
		resetPagination() {
			this.filters.paging = {
				skip: 0,
				take: 20,
			};
		},
		setActiveEvents(events: number[]) {
			this.activeEvents = events;
		},
		calculateDisplayMarkets(markets: { name: string; displayName: string; filterIndex: number }[] | undefined) {
			try {
				if (window.innerWidth >= 1280) {
					this.displayMarkets = markets && markets.length > 3 ? [markets[0], markets[1], markets[2]] : markets || [];
				} else if (window.innerWidth >= 1024 && window.innerWidth <= 1280 && markets?.length === 2) {
					this.displayMarkets = [markets[0], markets[1]];
				} else {
					this.displayMarkets = markets && markets.length > 0 ? [markets[0]] : [];
				}
			} catch (e) {
				this.displayMarkets = markets && markets.length > 3 ? [markets[0], markets[1], markets[2]] : markets || [];
			}
		},
		setEvents(events: IEvent[], keepExisting?: boolean) {
			const mappedEvents = new Map<number, IEvent>();
			events.forEach((event) => {
				const { eventId } = event;
				mappedEvents.set(eventId, event);
			});
			if (keepExisting) {
				this.events = new Map([...this.events, ...mappedEvents]);
			} else this.events = mappedEvents;
		},
		setMarkets(markets: IMarket[], keepExisting?: boolean) {
			const mappedMarkets = new Map<string, IMarket>();
			markets.forEach((market) => {
				const { marketId } = market;
				mappedMarkets.set(marketId, market);
			});
			if (keepExisting) {
				this.markets = new Map([...this.markets, ...mappedMarkets]);
			} else this.markets = mappedMarkets;
		},
		setOutcomes(outcomes: IOutcome[], keepExisting?: boolean) {
			const mappedOutcomes = new Map<string, IOutcome>();
			outcomes.forEach((outcome) => {
				const { outcomeId } = outcome;
				mappedOutcomes.set(outcomeId, { ...outcome, marketId: outcome.marketId });
			});
			if (keepExisting) {
				this.outcomes = new Map([...this.outcomes, ...mappedOutcomes]);
			} else this.outcomes = mappedOutcomes;
		},
		setPrices(prices: IPrice[], keepExisting?: boolean) {
			const mappedPrices = new Map<string, IPrice>();
			prices.forEach((price) => {
				const { outcomeId } = price;
				mappedPrices.set(outcomeId, price);
			});
			if (keepExisting) {
				this.prices = new Map([...this.prices, ...mappedPrices]);
			} else this.prices = mappedPrices;
		},
		setScores(scores: IScore[], keepExisting?: boolean) {
			const mappedScores = new Map<number, IScore>();
			scores.forEach((score) => {
				const { eventId } = score;
				mappedScores.set(eventId, score);
			});
			if (keepExisting) {
				this.scores = new Map([...this.scores, ...mappedScores]);
			} else this.scores = mappedScores;
		},
		setRegions(regions: IRegion[], keepExisting?: boolean) {
			const mappedRegions = new Map<string, IRegion>();
			regions.forEach((region) => {
				const { regionId } = region;
				if (region.leagues.length) mappedRegions.set(regionId, region);
			});
			if (keepExisting) {
				this.regions = new Map([...this.regions, ...mappedRegions]);
			} else this.regions = mappedRegions;
		},
		isFavourite(id: string, favouriteType: "League" | "Market") {
			if (favouriteType === "League") {
				return !!this.favourites.league.find((league) => league.id === id);
			}

			return !!this.favourites.market.find((market) => market.title === id);
		},
		addFavourite(favourite: { id: string; region?: string; sportId: string; title: string; favouriteType: "League" | "Market" }) {
			api<{ userAccountFavourites: IFavourite[] }>({
				url: "/UserAccountFavourite/AddUserAccountFavourite",
				type: "player",
				options: { method: "POST" },
				data: {
					id: favourite.id,
					title: favourite.title,
					favouriteType: favourite.favouriteType,
					sportId: favourite.sportId,
					...(favourite.favouriteType === "League" && { region: favourite.region }),
					sortOrder: 0,
				},
			}).then((res) => {
				if (favourite.favouriteType === "League" && res.userAccountFavourites.length)
					this.favourites.league = [res.userAccountFavourites[0], ...this.favourites.league];
				else if (res.userAccountFavourites.length) this.favourites.market = [res.userAccountFavourites[0], ...this.favourites.market];
			});
		},
		removeFavourite(id: string, favouriteType: "League" | "Market") {
			// biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
			let fav;
			if (favouriteType === "League") {
				fav = this.favourites.league.find((league) => league.id === id);
				this.favourites.league = this.favourites.league.filter((fav) => fav.id !== id);
			} else {
				fav = this.favourites.market.find((market) => market.title === id);
				this.favourites.market = this.favourites.market.filter((fav) => fav.title !== id);
			}
			api({
				url: "/UserAccountFavourite/RemoveUserAccountFavorite",
				type: "player",
				options: { method: "POST" },
				data: {
					userAccountFavourites: [fav],
				},
			});
		},
		setEvent(event: IEvent) {
			const storeEvent = this.events.get(event.eventId);
			if (event.version > 0 ? event.version >= (storeEvent?.version || 0) : true) {
				const updatedEvent = { ...event, homeTeam: storeEvent?.homeTeam || event.homeTeam, awayTeam: storeEvent?.awayTeam || event.awayTeam };
				this.events.set(event.eventId, updatedEvent);
			}
		},
		setMarket(market: IMarket) {
			const storeMarket = this.markets.get(market.marketId);
			if (market.version > 0 ? market.version >= (storeMarket?.version || 0) : true) {
				if (storeMarket) {
					this.markets.set(market.marketId, {
						...market,
						index: storeMarket.index,
						name: storeMarket.name,
						displayName: storeMarket.displayName,
						marketId: storeMarket.marketId,
						additionalInfo: storeMarket.additionalInfo,
					});
				} else this.markets.set(market.marketId, market);
			}
		},
		setOutcome(outcome: IOutcome) {
			const storeOutcome = this.outcomes.get(outcome.outcomeId);
			if (outcome.version > 0 ? outcome.version >= (storeOutcome?.version || 0) : true) {
				if (storeOutcome)
					this.outcomes.set(outcome.outcomeId, {
						...outcome,
						index: storeOutcome.index,
						marketId: storeOutcome.marketId,
						originalMarketId: storeOutcome.originalMarketId,
						displayName: storeOutcome.displayName,
					});
				else this.outcomes.set(outcome.outcomeId, outcome);
			}
		},
		setPrice(price: IPrice) {
			const storePrice = this.prices.get(price.outcomeId);
			if (price.version > 0 ? price.version >= (storePrice?.version || 0) : true) this.prices.set(price.outcomeId, price);
		},
		setScore(score: IScore) {
			this.scores.set(score.eventId, score);
		},
		handleProducerStatus(status: IProducer) {
			this.producerStatus = {
				...this.producerStatus,
				[status.producerId]: status.isUp,
			};
		},
	},
	getters: {
		isProducerActive: (state) => (event: IEvent) => {
			if (event.sportSpecificProperties?.Producer && state.producerStatus[event.sportSpecificProperties.Producer] !== undefined) {
				return state.producerStatus[event.sportSpecificProperties.Producer];
			}

			return true;
		},
		isEventActive: (state) => (event: IEvent) => {
			if (event.sportSpecificProperties?.Producer && state.producerStatus[event.sportSpecificProperties.Producer] !== undefined) {
				return state.producerStatus[event.sportSpecificProperties.Producer];
			}

			return event.isActive;
		},
		isStreamingEnabledForLeague:
			(state) =>
			(sportId: string, leagueId: string, regionId: string, isLive = false) => {
				const settings = useSettings();
				const streamingEnabled = settings.hasFeature("IsStreamingEnabled");
				const streamingEnabledForLeague = state.streamableLeagues?.[sportId]
					? state.streamableLeagues[sportId].findIndex((sl) => sl.leagueId === leagueId && sl.sportCountry === regionId) !== -1 || false
					: false;
				return streamingEnabled && isLive && streamingEnabledForLeague;
			},
		getMarketGroupings: (state) => (isEsports: boolean) => {
			const route = useRoute();
			const fixtureType = route.params.fixtureType;
			const filterApplied = !!state.filters.regionAndLeagueIds && fixtureType === "highlights";
			const flatLeagues = filterApplied ? state.filters.regionAndLeagueIds?.map((x) => `${x.regionId}_${x.leagueId}`) : undefined;
			const eventIds = state.pointers.events;
			const events = eventIds.map((x) => state.events.get(x)) as IEvent[];
			const markets = isEsports
				? groupBy(
						events.filter((x) => (filterApplied && flatLeagues?.length ? flatLeagues?.includes(`${x.regionId}_${x.leagueId}`) : x)),
						"league"
				  )
				: groupBy(
						events.filter((x) => (filterApplied && flatLeagues?.length ? flatLeagues?.includes(`${x.regionId}_${x.leagueId}`) : x)),
						(event) => `${event.leagueId}-${event.regionId}`
				  );
			return markets;
		},
		getEvent: (state) => (eventId: number) => {
			const event = state.events.get(eventId);
			const score = state.scores.get(eventId);
			const markets = [...state.markets.values()].filter((market) => market.eventId === eventId);
			const outcomes = groupBy(
				[...state.outcomes.values()].filter((outcome) => outcome.eventId === eventId),
				"marketId"
			);
			const prices = keyBy(
				[...state.prices.values()].filter((price) => price.eventId === eventId),
				"outcomeId"
			);
			return {
				...event,
				score,
				markets,
				outcomes,
				prices,
			};
		},
	},
	persist: {
		paths: ["oddsFormat"],
		storage: persistedState.localStorage,
	},
});
